import type {
  DehydratedViewportsConfigurations,
  StudyGroup,
} from '../ViewportsConfigurations/types';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { useSplitFlag, FF } from 'modules/feature-flags';
import {
  isAtLeastPhase4,
  isPhase4Steps,
} from 'domains/viewer/Viewer/StudiesBar/HangingProtocols/constants';
import { useStudies, useBaseViewerData } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { usePreviewHangingProtocolConfiguration } from './usePreviewHangingProtocol';
import {
  useSelectedHangingProtocol,
  useSelectedHangingProtocolWriters,
} from './useSelectedHangingProtocol';
import type { GetHangingProtocolMatchQuery } from 'generated/graphql';
import {
  publishStepHangingProtocol,
  useViewportStepWriters,
} from 'domains/viewer/ViewportsConfigurations';
import type { ViewportsConfigurations } from 'domains/viewer/ViewportsConfigurations';
import { FLAT_BUCKET } from '../ViewportsConfigurations/stepBuckets';
import type { Layouts } from '../Viewer/StudyLoader/viewerLoaderState';
import {
  clearUserLayoutOverride,
  usePublishProtocolLayouts,
} from '../Viewer/StudyLoader/viewerLayoutWriters';
import {
  useViewportsConfigurationsSelector,
  activeHangingProtocolSmidState,
} from '../ViewportsConfigurations/state';
import { ViewType } from 'generated/graphql';
import { logger } from 'modules/logger';
import { hydrateViewportsConfigurations } from '../ViewportsConfigurations/manipulators';
import { WithApolloContext } from 'modules/Apollo/types';
import {
  hangingProtocolStepIndex,
  hangingProtocolStepIndexAttributed,
  steppedHPConfigurations,
  steppedHPInitSettledFor,
  nextStepInitSettledStamp,
  editHeldHangingProtocolSelector,
  clinicalStepsBaseline,
  getStepsFromVersioned,
  unsavedClinicalStepEdits,
  makeHPKey,
} from './state';
import {
  applyServerBaselineOnly,
  applyServerSteps,
  useClinicalStepWriters,
} from './clinicalStepStore';
import {
  beginInvalidationRefetch,
  decideActiveSmidWrite,
  decideStepIndexHoming,
  decideStepInitPass,
  endInvalidationRefetch,
  getInvalidationRefetchesInFlight,
  noteActiveSmidGuardInvalidated,
  noteHangingProtocolDeleted,
  noteHPInvalidated,
  subscribeToInvalidationRefetches,
} from './hpInitMachine';
import { useIsUnsavedStepEditsHydrated } from './unsavedStepEditsHydration';
import { installHPMutationTraceSwitch, traceHPMutation } from './hpMutationTrace';
import { useMirrorUnsavedStepEdits } from './hooks/useMirrorUnsavedStepEdits';
import {
  resolveEditHeldHangingProtocol,
  useHoldHangingProtocolWithUnsavedEdits,
} from './hooks/useHoldHangingProtocolWithUnsavedEdits';
import { hasPersistedSteps, stepFromGqlFormat } from './steppedHPTypes';
import {
  fetchedStepsWouldLoseSteps,
  getPendingStepDeleteVersion,
  hasPendingStepCreates,
  pendingStepDeleteSmids,
  subscribeToPendingStepDeletes,
} from './pendingStepMutations';
import { mergeUnsavedStepEdits } from './hooks/useClinicalStepDirtyState';
import type { StepConfiguration } from './steppedHPTypes';
import { rebuildHPFromStep, stepBucketAt } from './stepUtils';
import type { HangingProtocol } from './stepUtils';
import { hpInvalidationChannel, type HPInvalidationMessage } from './broadcastHPInvalidation';
import { useViewerId } from 'hooks/useViewerId';
import { useCurrentCaseId } from 'hooks/useCurrentCase';

// Re-export for backward compatibility with existing consumers
export { broadcastHPInvalidation } from './broadcastHPInvalidation';
// Re-export for backward compatibility with existing consumers
export type { HangingProtocol } from './stepUtils';
export { rebuildHPFromStep } from './stepUtils';

// Module-level variable to track analytics across all hook instances.
// This prevents duplicate analytics events when multiple components call useHangingProtocol().
// The key combines hanging protocol smid and first study smid to ensure analytics fire
// when the same protocol is applied to different cases.
// Exported for testing purposes only.
export let __lastTrackedHangingProtocolKey: string | null = null;

// Reset function for tests
export const __resetLastTrackedHangingProtocolKey = (): void => {
  __lastTrackedHangingProtocolKey = null;
};

// The state that answers "what have we already done for this (case, HP) pair?" lives in
// `hpInitMachine.ts`. It used to be six `__` guards declared here and read inline in the middle of
// the init effect below, where nothing made their ordering visible -- which is what VX-4838 got
// wrong (VX-4810 slice 5). The names below are kept as the public vocabulary the rest of the app
// already calls; the guards themselves are no longer reachable from outside the machine.
export {
  /** Re-exported under its original name: `HangingProtocolWidget` and several test mocks call it. */
  noteHPInvalidated as invalidateHPInitializationCache,
  noteActiveSmidGuardInvalidated as invalidateHPSmidGuard,
  noteHangingProtocolDeleted as markHangingProtocolDeleted,
} from './hpInitMachine';

export type UseHangingProtocol = {
  loadingHangingProtocol: boolean;
  hangingProtocol: Nullable<HangingProtocol>;
  hangingProtocolViewportsConfigurations: Nullable<ViewportsConfigurations>;
  hangingProtocolLayouts: Nullable<Layouts>;
  resetToHP: () => void;
};

const useMatchedHangingProtocol = ({
  groupedStudies,
  loading,
  studyDescriptions,
  cases,
  context,
}: {
  cases: Array<Array<string>>;
  groupedStudies: Array<StudyGroup>;
  loading: boolean;
  studyDescriptions: Array<string>;
} & WithApolloContext): [
  GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies'],
  boolean,
] => {
  // `gbvdLoading` is GBVD's load state; `hangingProtocolLoading` is the
  // standalone HP-match query's. Adding/removing a prior re-fires only the HP
  // query (GBVD vars are stable), so we must surface its loading too —
  // otherwise the HP/layout UI renders as settled while the match is in flight.
  const {
    hangingProtocol,
    loading: gbvdLoading,
    hangingProtocolLoading,
  } = useBaseViewerData({ context });
  return [hangingProtocol, gbvdLoading || hangingProtocolLoading];
};

// Publish `window.__SIRONA_HP_TRACE__` at module load, so the switch exists before anyone goes
// looking for it. Idempotent and free -- see `hpMutationTrace.ts` for what it turns on and why the
// trace is off by default.
installHPMutationTraceSwitch();

export const useHangingProtocol = ({ context }: WithApolloContext): UseHangingProtocol => {
  const viewerId = useViewerId();
  const isPrimaryWindow = viewerId === '0';
  const currentCaseId = useCurrentCaseId();
  // Re-renders every mounted instance when an invalidation refetch starts or finishes, so the init
  // effect below can retry a publish it deferred.
  const invalidationRefetchesInFlight = useSyncExternalStore(
    subscribeToInvalidationRefetches,
    getInvalidationRefetchesInFlight,
    getInvalidationRefetchesInFlight
  );
  // Re-renders when any window begins or ends a pending step deletion, so the bucket prune below can
  // re-run. A committed deletion changes nothing else the prune depends on, so without this the bucket
  // it spared during the undo window outlived the deletion in every window but the deleting one.
  const pendingStepDeleteVersion = useSyncExternalStore(
    subscribeToPendingStepDeletes,
    getPendingStepDeleteVersion,
    getPendingStepDeleteVersion
  );
  const previewedHangingProtocol = usePreviewHangingProtocolConfiguration();
  const { loading, groupedStudies, refetch, invalidateHangingProtocolMatch } = useStudies({
    context,
  });
  const [phase] = useSplitFlag(FF.HANGING_PROTOCOL_PHASE);
  const isPhase4 = isAtLeastPhase4(phase);
  // Gate all Stepped-HP behavior on the phase-4-steps treatment. The step UI (StepBar,
  // useClinicalStepMode) is already gated on this phase; the hook must gate too,
  // otherwise a phase-4-steps-off user opening an HP that has step data would still get
  // the viewer rebuilt from step 0 (Split treatments are per-user/clinic, so
  // stepped HPs can exist in the DB while a given user's phase is not phase-4-steps).
  const isHPStepsEnabled = isPhase4Steps(phase);
  // The operational index -- what step to rebuild from -- comes from the selector, so a list that
  // renumbered under this window (a reorder or a delete, here or in the other window) moves this hook
  // with the reader's step rather than leaving it on whatever slid into the old slot. Reading the raw
  // `index` here would put the viewer's layout and viewports on a different step than the StepBar
  // selection resolves to.
  const stepIndex = useRecoilValue(hangingProtocolStepIndex);
  // The attribution is read alongside, and only for the two questions the number cannot answer: which
  // protocol positioned the index (the homing decision), and which step it names (the clamp below).
  // Both ride on the same broadcast message as the value, so both see the other window's navigation.
  const {
    index: storedStepIndex,
    stepSmid: selectedStepSmid,
    ownerHPKey: stepIndexOwnerHPKey,
  } = useRecoilValue(hangingProtocolStepIndexAttributed);

  // Stepped HP state - will be used when backend returns steps format
  // Both indices are owned by clinicalStepStore: a step-index write here is a publish decision with
  // the same inputs as a steps write, and this hook's init effect is the one that got it wrong
  // (VX-4810 3b).
  const {
    applyProtocolName,
    adoptServerViewportResolutions,
    selectStep: setStepIndex,
    selectSubStep: setSubStepIndex,
    // The only operation that changes which protocol the index belongs to. This hook is the one that
    // decides a protocol is new to the reader, so it is the one that records the answer.
    resetStepIndices: homeStepIndicesFor,
  } = useClinicalStepWriters();
  const { clearAllStepBuckets: clearAllStepBucketsBound, pruneStepBuckets: pruneStepBucketsBound } =
    useViewportStepWriters();
  const liveStepConfigurationsVersioned = useRecoilValue(steppedHPConfigurations);
  // A render-time mirror of the steps atom, for the init effect to read *without* depending on it.
  //
  // The adoption below needs the live steps to decide which baseline viewports it may fill, but
  // adding the atom to the init effect's dependency array makes that effect re-run on every steps
  // write -- and the effect stamps `steppedHPInitSettledFor`, which is the gate the bucket prune
  // waits on. Doing so opened that gate a beat early and pruned another case's buckets, caught by
  // `useHangingProtocol.test.tsx` ("does not prune buckets until init has settled"). A ref is always
  // current and changes no dependency.
  const liveStepsRef = useRef(liveStepConfigurationsVersioned);
  liveStepsRef.current = liveStepConfigurationsVersioned;
  const liveStepConfigurations = getStepsFromVersioned(liveStepConfigurationsVersioned);

  /**
   * Writes a freshly fetched step list into the viewer atoms, keeping steps the user has edited but
   * not saved (VX-4739, VX-4803). Reads the atoms synchronously so the merge sees the current values
   * rather than whatever this render closed over.
   *
   * **Returns whether the steps were published.** Two guards below refuse a response, and a caller
   * that goes on to reason about "the list the reader is now looking at" has to know which it got:
   * the refused response describes a protocol state that was explicitly judged not to be the live one,
   * so measuring anything against it is measuring against a list that never existed here.
   */
  const applyFetchedSteps = useRecoilCallback(
    ({ snapshot, set }) =>
      (
        stepConfigs: Nullable<StepConfiguration[]>,
        hpName: Nullable<string>,
        preserveUnsavedEdits: boolean,
        hpKeyForEdits: string
      ): boolean => {
        // An optimistic step create is outstanding, so this response predates it and applying it
        // would drop the step the user just added. Neither atom is written: the baseline has to move
        // with the steps or not at all, or the two end up describing different protocols.
        //
        // `mergeUnsavedStepEdits` does not cover it: that maps over the FETCHED steps to carry
        // unsaved *edits* onto server identities, so a step absent from the fetch disappears.
        //
        // How it happens: Reset All clears the init guard and refetches. When that refetch comes back
        // unchanged, no init pass runs, so nothing re-arms the guard and it stays open. The next
        // render is whatever the user does next -- pressing Add Step -- and the init pass fires there,
        // republishing the pre-add protocol over the optimistic step. The step vanishes even though
        // its create succeeded, so pressing Add Step again names a second step identically and both
        // show up after a refresh (VX-4811).
        //
        // Keyed on a real pending create rather than on the smid looking client-generated. A freshly
        // created protocol also carries `step-{uuid}` smids before its first reconcile, and
        // suppressing the re-initialization there stopped viewports resolving entirely.
        if (hasPendingStepCreates()) {
          logger.info('[HP] skipped a step re-init while an unsaved step was pending', {
            fetchedSteps: stepConfigs?.length ?? null,
          });
          // Only the steps are held back. The baseline still tracks the server, and the tempting
          // symmetry -- "skip the steps, so skip the baseline too" -- was tried and measured: it
          // drops the comprehensive live spec from 20 passing phases to 10, failing at "Phase 4c4:
          // Form open flushes pending save without data loss". A deferred save that flushes while a
          // create is in flight needs the baseline to match what the server now holds, or the step
          // it just saved reads as unsaved again.
          //
          // The two atoms are not describing different protocols in the meantime: the steps atom is
          // ahead by exactly the optimistic step, which is what the pending create is about to make
          // real, and `endPendingStepCreate` releases this guard as soon as it resolves.
          applyServerBaselineOnly(set, { serverSteps: stepConfigs });
          return false;
        }

        // A response that is missing a step we already hold, with a server smid and no deletion in
        // flight, is behind rather than authoritative. Applying it would republish the shorter list
        // at a higher version and destroy the step in every window (VX-4770). Unlike the pending-create
        // case above, the baseline is skipped too: there the server list is correct for what is
        // persisted, whereas here the server has the step and this copy of the response does not, so
        // baselining it would be recording something known to be stale.
        const liveStepsNow = getStepsFromVersioned(
          snapshot.getLoadable(steppedHPConfigurations).getValue()
        );
        if (
          fetchedStepsWouldLoseSteps({ fetchedSteps: stepConfigs ?? null, liveSteps: liveStepsNow })
        ) {
          logger.info('[HP] skipped a step re-init that would have dropped a persisted step', {
            fetchedSteps: stepConfigs?.length ?? null,
            liveSteps: liveStepsNow?.length ?? null,
          });
          return false;
        }

        const previousBaseline = snapshot.getLoadable(clinicalStepsBaseline).getValue();

        // Two routes reach the merge. An in-session re-read of the same HP compares the live atoms,
        // and may splice by position because a reorder re-keys the whole collection (VX-4741). A
        // fresh load has empty live atoms and compares the persisted record instead, with positional
        // splicing off: that record is sparse, and an unrecognised smid across a reload means the
        // collection was re-keyed between sessions, where position says nothing (VX-4803).
        const persisted = snapshot.getLoadable(unsavedClinicalStepEdits(hpKeyForEdits)).getValue();

        applyServerSteps(set, {
          serverSteps: stepConfigs ?? null,
          name: hpName,
          // Reads `previousSteps` rather than the callback's snapshot: an optimistic write can commit
          // between this callback being invoked and the updater running, and the snapshot would still
          // report the pre-write list.
          resolveSteps: (previousSteps) =>
            preserveUnsavedEdits
              ? mergeUnsavedStepEdits(stepConfigs ?? null, previousSteps, previousBaseline)
              : persisted != null
                ? mergeUnsavedStepEdits(stepConfigs ?? null, persisted.edits, persisted.baselines, {
                    allowPositionalFallback: false,
                  })
                : (stepConfigs ?? null),
        });
        return true;
      },
    []
  );

  const studyDescriptions: string[] = useMemo(
    () =>
      groupedStudies.flatMap((studies) =>
        studies.reduce<string[]>(
          (studies, { description }) => (description != null ? [...studies, description] : studies),
          []
        )
      ),
    [groupedStudies]
  );
  const cases = useMemo(() => {
    return groupedStudies.map((group) => group.map((study) => study.smid));
  }, [groupedStudies]);

  const [matchedHangingProtocol, matchedHangingProtocolLoading] = useMatchedHangingProtocol({
    groupedStudies,
    loading,
    studyDescriptions,
    cases,
    context,
  });

  // The HP the user has chosen for this case, resolved to a payload. Nothing here is a stored copy
  // of the protocol: the selection is an smid, and the payload behind it is either the server match
  // (when they are the same HP) or a fetch keyed by that smid. So there is no snapshot frozen at
  // choice time that can outrank fresh server data, and no caller has to re-pin the smid after a
  // refetch to keep the choice (VX-4809).
  const selectedHangingProtocol = useSelectedHangingProtocol({
    matchedHangingProtocol,
    matchedHangingProtocolLoading,
    cases,
    context,
  });
  const { refreshSelectedHangingProtocol } = useSelectedHangingProtocolWriters({ cases, context });

  // The protocol this case is held on because the reader has unsaved step edits in it, which only
  // stands in when the match names a different one (VX-4872). Ranked below the selection: an explicit
  // choice is the reader saying which protocol they want, and it must outrank a hold left over from
  // edits parked on the one they left.
  const editHeldHangingProtocol = resolveEditHeldHangingProtocol({
    editHeldHangingProtocol: useRecoilValue(editHeldHangingProtocolSelector(currentCaseId)),
    matchedHangingProtocol,
  });

  const originalHangingProtocol = useMemo(() => {
    const [source, protocol] =
      previewedHangingProtocol != null
        ? (['preview', previewedHangingProtocol] as const)
        : selectedHangingProtocol != null
          ? (['selected', selectedHangingProtocol] as const)
          : editHeldHangingProtocol != null
            ? (['held', editHeldHangingProtocol] as const)
            : (['matched', matchedHangingProtocol] as const);
    // Which source supplied the applied protocol, and how many steps it carries. A stale step list
    // published after a save names its source here, which the steps-write trace alone cannot.
    traceHPMutation('appliedProtocol', () => ({
      source,
      smid: protocol?.smid ?? null,
      steps: protocol?.steps?.length ?? null,
    }));
    return protocol;
  }, [
    editHeldHangingProtocol,
    matchedHangingProtocol,
    previewedHangingProtocol,
    selectedHangingProtocol,
  ]);

  const hpSmid = originalHangingProtocol?.smid ?? null;

  // Key the guard on case + HP smid. The same HP smid can match different cases, so keying on the
  // smid alone would skip re-init when navigating to a new case that matches the same HP, leaving
  // stale step state and a possibly out-of-bounds stepIndex (VX-4429).
  const hpKey = useMemo(() => makeHPKey(currentCaseId, hpSmid), [currentCaseId, hpSmid]);

  // Subscribing is what mounts this family member's persister effect, and therefore what eventually
  // fires its onHydrated. The value itself is read inside applyFetchedSteps from the snapshot rather
  // than from here, so it cannot be a render behind (VX-4803).
  useRecoilValue(unsavedClinicalStepEdits(hpKey));
  const isUnsavedStepEditsHydrated = useIsUnsavedStepEditsHydrated(isHPStepsEnabled ? hpKey : null);

  useMirrorUnsavedStepEdits(isHPStepsEnabled ? hpKey : null);

  // Holds the applied protocol against a later change in the server match, from the reader's first
  // unsaved step edit onwards (VX-4872). Disabled while a preview is up: the step atoms then describe
  // the preview, so their divergence says nothing about the protocol underneath it, and the preview
  // flow already has its own guard for unsaved edits.
  useHoldHangingProtocolWithUnsavedEdits({
    caseSmid: currentCaseId,
    hangingProtocol: originalHangingProtocol,
    enabled: isHPStepsEnabled && previewedHangingProtocol == null,
  });

  const setSteppedHPInitSettledFor = useSetRecoilState(steppedHPInitSettledFor);
  // Read as well as written: bucket pruning is gated on it, to prove the global steps atom has been
  // republished for the case and protocol the prune is about to delete against.
  const settledForHPKey = useRecoilValue(steppedHPInitSettledFor);

  // Runs first on an hpKey change (it's declared before the init-machine effect below, and both
  // effects share hpKey as a dep, so React fires them in that order on the same commit). Nulling the
  // flag here -- rather than leaving whatever the previous key stamped it with -- is what keeps the
  // bucket key honest about a case switch: without it, a new case's still-unhydrated record would
  // read as settled off the previous case's stamp, and a revisited case's record hydrates *instantly*
  // (unsavedStepEditsHydration.ts never forgets an hpKey it has already seen), so it would read
  // settled before the init-machine effect below has re-published its steps.
  //
  // A layout effect, not a passive one: `activeStepBucketSelector` only checks that the stamp is
  // non-null, never that it names the *current* hpKey, so correctness rests entirely on this null
  // having landed before the first read of the new key. A passive null runs after paint, so the
  // render that first sees a new `hpKey` would still read the previous protocol's stamp -- and if
  // `steppedHPConfigurations` (a single shared atom) happens to resolve to a bucket that also exists
  // in the new case's record, trivially true for FLAT_BUCKET, a stale bucket's contents render for
  // one paint. `useLayoutEffect` runs before paint, so React re-renders off the null before anything
  // reaches the screen. Only ever writing `null` keeps this safe to move earlier: it can only widen
  // the window where the bucket reads as unsettled, which is the conservative direction, never the
  // window where a stale bucket is mistaken for a current one.
  // Only un-settles a *different* key. Four instances of this hook are mounted in a viewer (widget,
  // layouts, viewport overlays) and they share this atom, so an unconditional null here meant any
  // instance mounting after init had settled -- a later overlay, a StepBar remount during step
  // navigation -- wiped a stamp that was already correct for the key it was mounting into. Whether it
  // came back then depended on that instance's own decision: `sync-name-only` re-stamps, but
  // `await-hydration` does not, and its hydration signal may already have fired before it subscribed,
  // so the stamp could stay null for good. With the stamp null, `activeStepBucketSelector` yields no
  // bucket, and the reader treats that as no overrides *and* no protocol viewports -- the grid hangs
  // its images on the first paint and then blanks.
  //
  // Guarding on the key keeps the original purpose intact: a genuinely new (case, HP) must not inherit
  // the previous one's stamp. It also makes the atom's contents honest -- it now holds either null or
  // the key it actually describes, which is what `activeStepBucketSelector` assumes when it checks
  // only for non-null.
  useLayoutEffect(() => {
    setSteppedHPInitSettledFor((previous) => nextStepInitSettledStamp(previous, hpKey));
  }, [hpKey, setSteppedHPInitSettledFor]);

  useEffect(() => {
    // One decision, taken inside `hpInitMachine`, where every guard read and every guard write for
    // this pass happens in one fixed order. That order is the product of VX-4838, VX-4770, VX-4783,
    // VX-4738 and VX-4811; VX-4838 in particular was not a bug in either guard it involved but in the
    // order between them, and nothing in the types made that visible while it lived here. The switch
    // below is exhaustive, so a new decision kind is a compile error until it is handled.
    const decision = decideStepInitPass({
      hpKey,
      hpSmid,
      isHPStepsEnabled,
      isUnsavedStepEditsHydrated,
      // The subscribed value this render was built from, deliberately not the machine's live counter.
      // The decision has to match the render that scheduled it: a refetch that starts between render
      // and effect must not change the verdict, or the effect stops being a function of its deps.
      refetchesInFlight: invalidationRefetchesInFlight,
      // Equivalent to `stepConfigs != null` below, without paying for the conversion on passes that
      // bail. It decides only whether preserving unsaved edits is possible at all. A length check
      // alone is not enough: /hang-cases returns a single synthetic step (`smid: null`) for a flat
      // HP, so `hasPersistedSteps` is what actually distinguishes a real stepped HP (VX-4889).
      hasFetchedSteps: hasPersistedSteps(originalHangingProtocol?.steps),
    });
    traceHPMutation('stepInit.decision', () => ({
      kind: decision.kind,
      preserve: decision.kind === 'publish' ? decision.preserveUnsavedEdits : null,
      smid: hpSmid,
      fetchedSteps: originalHangingProtocol?.steps?.length ?? null,
    }));

    switch (decision.kind) {
      // Flag off and an HP that no longer exists both leave `steppedHPConfigurations` exactly as
      // correct for this hpKey as it already was -- the flag-off default never gets touched, and
      // ignore-deleted is a stale response the machine has already decided to discard, not a change
      // to the atom. So both count as settled for this key.
      case 'disabled':
      case 'ignore-deleted':
        setSteppedHPInitSettledFor(hpKey);
        return;

      // Waiting states. The atom either has not been decided for this key yet (await-hydration) or
      // is about to be superseded by a pass this one deferred to (defer, ignore-transient-null), so
      // stamping the key here would tell a reader "correct" about data that is not yet, or no longer,
      // trustworthy.
      case 'await-hydration':
      case 'ignore-transient-null':
        return;

      case 'defer':
        logger.info(
          '[HP] deferred a step re-init that would have published pre-invalidation data',
          {
            hangingProtocolSmid: originalHangingProtocol?.smid,
            staleSteps: originalHangingProtocol?.steps?.length ?? null,
          }
        );
        return;

      case 'sync-name-only': {
        // A rename doesn't touch `.steps`, so it must not be blocked by the protection that keeps a
        // refetch from clobbering in-progress local step edits. Without this, a window that didn't
        // perform the rename itself (e.g. viewer/1 picking up a broadcast-triggered refetch) would
        // fetch the correct name from the server and keep showing the stale one.
        const newName = originalHangingProtocol?.name ?? null;
        if (newName != null) {
          applyProtocolName({ name: newName });
        }

        // Same argument as the name, one field over: adopt the viewport *identities* this response
        // resolved, for the viewports this window is holding as unresolved.
        //
        // A stepped protocol is stored criteria-only, so the steps a form save flushes carry null
        // study/series/stack smids. `preserveResolvedViewports` carries the previous resolution onto
        // any viewport whose rule did not change and deliberately leaves a *retargeted* one
        // unresolved, described there as "left unresolved for the next match". This is that match --
        // and without this line nothing delivers it, because the guard that produced this very
        // `sync-name-only` decision is what stops the publish path from running. The viewport stays
        // blank across a reload (VX-5124).
        //
        // Safe on this branch precisely because it is not a publish: it fills identities and touches
        // nothing else, only where the viewport holds nothing and both sides carry the same rule, so
        // it cannot overwrite an in-progress local step edit. See `adoptServerViewportResolutions`.
        const resolvedFromServer = originalHangingProtocol?.steps;
        if (resolvedFromServer != null && resolvedFromServer.length > 0) {
          adoptServerViewportResolutions({
            serverSteps: resolvedFromServer.map(stepFromGqlFormat),
            // The live steps decide which baseline viewports are eligible: a viewport the reader has
            // edited must keep its baseline, or adopting an identity there clears a dirty indicator
            // the reader still needs. See `baselineResolutionUpdatesFor`.
            localSteps: getStepsFromVersioned(liveStepsRef.current),
          });
        }

        setSteppedHPInitSettledFor(hpKey);
        return;
      }

      case 'publish':
        break;
    }

    // The publish path below is about to bring `steppedHPConfigurations` in line with this hpKey.
    setSteppedHPInitSettledFor(hpKey);

    // Convert GQL array-format steps to internal Record-based format.
    // Treat a flat HP's steps as null so it doesn't present as stepped -- an empty array is not the
    // only shape that means "flat": /hang-cases returns a single synthetic step (`smid: null`) for a
    // flat HP, so a length check alone lets it through (VX-4889).
    const stepConfigs = hasPersistedSteps(originalHangingProtocol?.steps)
      ? (originalHangingProtocol?.steps ?? []).map(stepFromGqlFormat)
      : null;

    // Order and membership always come from the server, so a reorder lands; when the machine says to
    // preserve, only the content of steps with unsaved edits is kept, and the baseline is still the
    // server's copy, which is what makes those preserved edits read as dirty (VX-4739). A fresh page
    // load never invalidates the guard, so it takes the persisted-record route inside
    // `applyFetchedSteps` instead (VX-4803).
    const didPublishSteps = applyFetchedSteps(
      stepConfigs,
      originalHangingProtocol?.name ?? null,
      decision.preserveUnsavedEdits,
      hpKey
    );
    traceHPMutation('stepInit.published', { didPublishSteps, steps: stepConfigs?.length ?? null });

    // Every genuine HP change (case transition, preview accept, switch, delete-to-default) should
    // land on the first step; a re-read has to keep the reader where they are, because it can arrive
    // hundreds of milliseconds after the UI settled and they clicked a step (VX-4783).
    //
    // The index's own `ownerHPKey` is what separates the two, so this is a question about the state
    // rather than about what this window happens to have done. `stepIndexOwnerHPKey` is read from the
    // atom, which is broadcast, so a step the reader selected in the *other* window counts -- which is
    // the case no per-window guard could see.
    const homing = decideStepIndexHoming({
      hpKey,
      hpSmid,
      indexOwnerHPKey: stepIndexOwnerHPKey,
    });

    if (homing.kind === 'home') {
      // Records the protocol as the index's owner in the same write. That record is what makes the
      // *next* pass for this protocol a re-read: it replaces the `__lastStepIndexHomedHPKey` claim the
      // old code made here, with the difference that it rides on the broadcast index and so is visible
      // to the other viewer window too.
      homeStepIndicesFor({ forHPKey: hpKey });
      return;
    }

    // Same protocol re-read: the only index change owed here is a clamp, in case the response
    // describes fewer steps than the index points at (a step deleted in another window).
    //
    // A clamp lands on a different step, and sub-step indices are per-step -- leaving subStepIndex
    // where it was would point it at sub-steps the new step does not have. Reset it alongside, the
    // way the home branch above does.
    //
    // **Only ever clamp against a response that was actually published.** `applyFetchedSteps` has two
    // guards that refuse one -- a pending optimistic create, and a response missing a step this window
    // holds with a server smid (VX-4770) -- and in both cases the steps atom is left exactly as it was.
    // Clamping anyway measures the reader's position against a list the same function had just judged
    // too stale to apply, and moves them onto a step of a protocol state that was never adopted here.
    // The pending-create case is the VX-4811 shape: press Add Step, and the index legitimately points
    // one past the end at the step that was just created, so a clamp takes the reader straight off it.
    //
    // Asking `applyFetchedSteps` what it did, rather than re-deriving its guards here, is the point.
    // A duplicated guard is a guard that drifts, and this is the second review round in which one of
    // these two skip paths was missed by the copy at this call site.
    //
    // Given a real publish, the bounds test is against the *stored* number, not the resolved one, and
    // only when identity cannot answer. `stepIndex` was resolved during the render that scheduled this
    // effect, i.e. against the pre-publish list, so testing it here asks about a list that no longer
    // exists. And if the selected step is in the published list there is nothing to clamp however the
    // numbers fall: the selector resolves identity and lands the reader on their own step. Only when it
    // cannot does the stored number become the reader's position, and only then can it be out of range.
    const stepCount = stepConfigs?.length ?? 0;
    const isSelectedStepPublished =
      selectedStepSmid != null && (stepConfigs?.some((s) => s.smid === selectedStepSmid) ?? false);
    if (
      didPublishSteps &&
      stepCount > 0 &&
      !isSelectedStepPublished &&
      storedStepIndex >= stepCount
    ) {
      setStepIndex(stepCount - 1);
      setSubStepIndex(0);
    }
  }, [
    isHPStepsEnabled,
    originalHangingProtocol,
    adoptServerViewportResolutions,
    hpSmid,
    hpKey,
    isUnsavedStepEditsHydrated,
    storedStepIndex,
    selectedStepSmid,
    stepIndexOwnerHPKey,
    setStepIndex,
    applyProtocolName,
    applyFetchedSteps,
    setSubStepIndex,
    homeStepIndicesFor,
    invalidationRefetchesInFlight,
    setSteppedHPInitSettledFor,
  ]);

  // A protocol switch on the same case leaves buckets keyed by steps that are no longer
  // addressable, and the new step list has not arrived yet to prune against -- so clear wholesale
  // and let the resolve below repopulate.
  //
  // Tracking hpSmid alone is not enough: it also transitions null -> value on the *first*
  // resolution of every case load, and again on a case switch (case A/hp1 -> case B/hp2). Both of
  // those have nothing to strand -- the first has no prior protocol on this case, the second's
  // buckets belong to a different case entirely -- so clearing on either wipes a persisted record
  // that was just hydrated instead of the stale one this effect exists to drop. Only a switch
  // between two resolved protocols on the *same* case strands anything.
  // Deliberately not extended to cover the protocol going away (`hpSmid` null). That is VX-5091 and
  // it is fixed in the delete handler instead -- `discardStepViewportOverrides` in
  // `HangingProtocolWidget` -- because this effect is the wrong shape for it in two ways, both
  // measured on a PR preview rather than argued:
  //
  // - `clearAllStepBuckets` also empties `dehydratedHangingProtocol`, and only the primary window
  //   publishes that atom. Clearing it here runs in *every* window, so viewer/1 lost its protocol
  //   base with nothing able to put it back, and its whole grid went blank while still showing the
  //   step bar. What VX-5091 strands is the *override* layer; the derived base is not the problem
  //   and must not be collateral.
  // - The signal is wrong. A same-case null smid is far more often a transient refetch answer than a
  //   deletion -- `decideActiveSmidWrite` skips one for exactly that reason -- and this effect cannot
  //   tell them apart, so a destructive clear here fires on blips.
  //
  // The delete path has neither problem: it knows a delete is happening, it runs once, and it can
  // write the clear in the form that crosses windows.
  const previousHPKeyRef = useRef<{ caseId: string; hpSmid: string } | null>(null);
  useEffect(() => {
    const previous = previousHPKeyRef.current;
    if (currentCaseId == null || hpSmid == null) {
      previousHPKeyRef.current = null;
      return;
    }
    previousHPKeyRef.current = { caseId: currentCaseId, hpSmid };
    if (previous == null || previous.caseId !== currentCaseId || previous.hpSmid === hpSmid) return;
    clearAllStepBucketsBound({ caseId: currentCaseId });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clearAllStepBucketsBound is stable
  }, [currentCaseId, hpSmid]);

  // Deleting a step must not leave its overrides behind to be resurrected by a later step reusing
  // the smid. Cheap on the no-op path: pruneBuckets returns by reference when nothing was dropped.
  useEffect(() => {
    if (currentCaseId == null) return;
    // Only prune once init has settled for *this* case and protocol. `steppedHPConfigurations` is a
    // plain atom, not case-scoped, so on a case switch it still holds the previous case's steps while
    // `currentCaseId` has already moved on. Pruning in that window deletes the newly opened case's
    // persisted buckets for the sole reason that their smids do not appear in a list that was never
    // about that case -- and because the target atoms are persisted, the bad prune survives a reload.
    // The stamp is exactly the proof needed: it is nulled when the key changes and re-set only once
    // the steps atom has been republished for the new key.
    if (settledForHPKey == null || settledForHPKey !== hpKey) return;
    const steps = getStepsFromVersioned(liveStepConfigurationsVersioned);
    if (steps == null) return;
    pruneStepBucketsBound({
      caseId: currentCaseId,
      liveStepSmids: [
        // A step with no smid yet (optimistic create) has no bucket of its own to protect --
        // its overrides sit in FLAT_BUCKET until the server assigns one, and that bucket survives
        // pruneBuckets unconditionally.
        ...steps.flatMap((s) => (s.smid != null ? [s.smid] : [])),
        // A step whose deletion has not committed is still addressable, so its bucket has to stay.
        // The delete is optimistic: the step leaves the list immediately while the mutation waits out
        // the undo window, and both UNDO and Reset All put it back. Pruning on the optimistic removal
        // meant the step returned without the unsaved viewport overrides the reader had just made on
        // it. `executeDeleteOnBackend` clears the bucket once the deletion actually commits.
        ...pendingStepDeleteSmids(),
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pruneStepBucketsBound is stable
  }, [
    currentCaseId,
    hpKey,
    settledForHPKey,
    liveStepConfigurationsVersioned,
    pendingStepDeleteVersion,
  ]);

  // Listen for cross-window HP invalidation signals. When viewer/0 saves or
  // deletes an HP, it broadcasts so viewer/1 can refetch and re-initialize.
  const handleHPInvalidation = useCallback(
    async (deletedHPSmid?: string) => {
      // Record the deletion before refetching: this window's own in-flight and background queries
      // can still resolve with the deleted HP, and the init guard is about to be cleared (VX-4738).
      if (deletedHPSmid != null) noteHangingProtocolDeleted(deletedHPSmid);
      // Marked before the awaits and cleared in `finally`: the whole point is to cover the renders
      // that happen while the refetch is outstanding, which is where the stale write lands.
      beginInvalidationRefetch();
      try {
        await invalidateHangingProtocolMatch();
        await refetch();
        // The match is not always the applied protocol: a selection outranks it, and is applied from
        // its cached payload whenever the match names a different HP (a criteria tie, or VX-5155, where
        // the backend does not match a protocol created from this case). Refreshing only the match left
        // that payload holding the pre-save copy, so this window re-initialized from it and published
        // it over the saving window's steps -- the VX-4770 failure below, reached through the
        // selection. A no-op when there is no selection.
        await refreshSelectedHangingProtocol();
      } finally {
        endInvalidationRefetch();
      }
      // Drop the guards only once the fresh protocol is in hand.
      //
      // Clearing them first opened a window hundreds of milliseconds wide -- the length of the
      // refetch -- in which this window would re-render with the guard down and the OLD protocol
      // still in `originalHangingProtocol`. The init effect then re-applied that stale copy and,
      // because every write bumps the shared version counter, broadcast it as newer than the fresh
      // steps the saving window had just sent us. The saving window took it, and the steps the user
      // had just added disappeared from both windows until a refresh (VX-4770).
      //
      // Measured: v/1 accepted v6/3steps, then re-initialised from its stale 1-step copy and emitted
      // v7/1steps, which v/0 accepted over its own correct v6/3steps.
      noteHPInvalidated();
      noteActiveSmidGuardInvalidated();
    },
    [invalidateHangingProtocolMatch, refetch, refreshSelectedHangingProtocol]
  );

  useEffect(() => {
    const handler = (message?: HPInvalidationMessage) => {
      // handleHPInvalidation is async; catch so a rejected refetch doesn't surface
      // as an unhandled promise rejection from the BroadcastChannel callback.
      handleHPInvalidation(message?.deletedHPSmid).catch((error) => {
        logger.warn('Failed to handle hanging-protocol invalidation broadcast', { error });
      });
    };
    // Use addEventListener (not onmessage) so multiple concurrent useHangingProtocol instances
    // (HangingProtocolWidget, useLayouts, viewport overlays) each register their own handler.
    // Assigning the single onmessage slot in each instance clobbered the others' handlers, so
    // cross-window HP invalidation could be silently dropped (VX-4430).
    hpInvalidationChannel.addEventListener('message', handler);
    return () => {
      hpInvalidationChannel.removeEventListener('message', handler);
    };
  }, [handleHPInvalidation]);

  // For stepped HPs, the GQL response has empty top-level data.layouts and
  // viewportsConfigurations (they live per-step instead). This memo replaces
  // those fields with the current step's values so downstream consumers
  // (getHangingProtocolLayouts, dehydrateHangingProtocol, etc.) work unchanged.
  // Uses liveStepConfigurations (Recoil atom) so renames and edits are reflected.
  const hangingProtocol = useMemo(() => {
    // Feature-flag gate (critical): with Stepped HP off, always return the
    // original (flat) HP so the viewer layout/viewports are never rebuilt from
    // step data. This is the definitive production-safety guard -- everything
    // downstream (dehydrateHangingProtocol, getHangingProtocolLayouts, the
    // dehydratedHangingProtocol atom write) derives from this value.
    if (!isHPStepsEnabled) {
      return originalHangingProtocol;
    }

    const stepConfigs = liveStepConfigurations;
    if (stepConfigs == null || stepConfigs.length === 0 || stepIndex >= stepConfigs.length) {
      return originalHangingProtocol;
    }

    // Guard against a cross-window step echo (steppedHPConfigurations syncs via
    // BroadcastChannel) arriving before this window's own HP query resolves.
    // Rebuilding from a null HP would produce an smid-less object and transiently
    // null activeHangingProtocolSmidState.
    if (originalHangingProtocol == null) {
      return originalHangingProtocol;
    }

    const stepConfig = stepConfigs[stepIndex];
    const rebuilt = rebuildHPFromStep(originalHangingProtocol, stepConfig);

    return rebuilt;
  }, [isHPStepsEnabled, originalHangingProtocol, liveStepConfigurations, stepIndex]);

  // The bucket this pass's resolved viewport assignments belong to. Mirrors the gates in the memo
  // above: with steps disabled, or with no step list yet, there is no step to attribute to, so
  // everything files under the flat bucket -- exactly what a non-stepped protocol always used.
  const activeBucket = useMemo(() => {
    if (!isHPStepsEnabled) return FLAT_BUCKET;
    return stepBucketAt(liveStepConfigurationsVersioned, stepIndex);
  }, [isHPStepsEnabled, liveStepConfigurationsVersioned, stepIndex]);

  const hangingProtocolViewportsConfigurations = useMemo(() => {
    return dehydrateHangingProtocol(hangingProtocol);
  }, [hangingProtocol]);

  const setHangingProtocol = useRecoilCallback(
    ({ set }) =>
      (
        caseSmid: Nullable<string>,
        bucket: string,
        value: Nullable<DehydratedViewportsConfigurations>
      ) => {
        if (caseSmid == null) return;
        publishStepHangingProtocol(set, { caseId: caseSmid, bucket, dehydrated: value });
      },
    []
  );
  const setHangingProtocolSmid = useSetRecoilState(activeHangingProtocolSmidState);
  useEffect(() => {
    // The HP widget only renders on the primary window (viewer/0). Only the primary
    // window should write to broadcast atoms to avoid infinite ping-pong between windows.
    // The secondary window receives values via broadcastChannelSynchronizerEffect.
    if (!isPrimaryWindow) return;
    // Note: This *should* be fine. Null is the default, but should not occur again after the initial query. Putting this gate fixes tricky issues with TestViewportWrapper.
    if (hangingProtocolViewportsConfigurations == null) return;

    setHangingProtocol(currentCaseId, activeBucket, hangingProtocolViewportsConfigurations);
  }, [
    isPrimaryWindow,
    currentCaseId,
    activeBucket,
    setHangingProtocol,
    hangingProtocolViewportsConfigurations,
  ]);

  // Set the HP smid independently of the dehydrated viewport configs.
  // The smid is available from hangingProtocol immediately, but viewport
  // configs may be delayed (async step rebuild, hydration). Gating the
  // smid on viewport configs caused createStepOnBackend and saveStepOrder
  // to silently skip backend calls (they guard on hpSmid being non-null).
  //
  // Like the effect above, only the primary window should write to this
  // broadcast atom — secondary windows consume it via
  // broadcastChannelSynchronizerEffect. Writing from both windows increases
  // cross-window chatter and can reintroduce multi-window race conditions.
  //
  // A getBaseViewerData refetch that transiently returns no matched HP (e.g. right after
  // a clinical step delete) would otherwise null this out immediately -- unlike the
  // BroadcastChannel path, which already rejects stale null echoes via its mergeFn (see
  // activeHangingProtocolSmidState in ViewportsConfigurations/state.ts), this local writer had
  // no equivalent guard. That let a single transient null cancel any in-flight operation
  // gated on hpSmid, e.g. the deferred step-deletion timer in useClinicalStepOperations. Skip a
  // null for the *same case* (almost certainly a transient refetch blip); a genuine case switch
  // to a no-HP case still clears it, since the case segment of the key changes.
  //
  // Deleting the currently-active HP is the one case where a same-case null IS legitimate (the
  // HP no longer exists, so the refetch's null/default-HP result must win). That's handled by
  // invalidateHPSmidGuard(), called alongside invalidateHPInitializationCache() wherever HP data
  // is invalidated (see the delete handler in HangingProtocolWidget.tsx and handleHPInvalidation
  // above) -- it drops the machine's record so the next null is written instead of skipped
  // (VX-4615 review). The skip/write rule itself lives in `decideActiveSmidWrite`.
  useEffect(() => {
    if (!isPrimaryWindow) return;
    const smid = hangingProtocol?.smid ?? null;
    if (!decideActiveSmidWrite({ currentCaseId, smid })) return;
    setHangingProtocolSmid(smid);
  }, [isPrimaryWindow, setHangingProtocolSmid, hangingProtocol?.smid, currentCaseId]);

  useEffect(() => {
    const hpSmid = hangingProtocol?.smid ?? null;
    const primaryStudySmid = groupedStudies[0]?.[0]?.smid ?? null;
    const trackingKey =
      primaryStudySmid != null && hpSmid != null ? `${primaryStudySmid}:${hpSmid}` : null;

    if (
      !isPhase4 ||
      trackingKey == null ||
      trackingKey === __lastTrackedHangingProtocolKey ||
      hangingProtocol == null
    ) {
      return;
    }

    const studies = groupedStudies.flat().map((study) => ({
      smid: study.smid,
      description: study.description ?? null,
      series_descriptions: study.seriesList?.map((s) => s.description).filter(Boolean) ?? [],
    }));

    analytics.track(viewer.usr.hangingProtocolApplied, {
      hanging_protocol_name: hangingProtocol.name ?? null,
      hanging_protocol_smid: hpSmid,
      has_owner: true,
      hanging_protocol_shape: {
        name: hangingProtocol.name ?? '',
        study_descriptions: hangingProtocol.studyDescriptions ?? [],
        number_of_priors: hangingProtocol.numberOfPriors ?? 0,
        study_criteria: hangingProtocol.studyCriteria ?? null,
        layouts: hangingProtocol.data?.layouts ?? [],
        viewports_configurations:
          hangingProtocol.viewportsConfigurations?.map((vc) => ({
            id: vc.id,
            view_type: vc.configuration.viewType,
            series_descriptions: vc.configuration.seriesDescriptions ?? [],
            series_criteria: vc.configuration.seriesCriteria ?? null,
            comparison_index: vc.configuration.comparisonIndex ?? null,
            matched_study_smid: vc.configuration.study?.smid ?? null,
            matched_series_smid: vc.configuration.series?.smid ?? null,
          })) ?? [],
      },
      studies,
    });

    __lastTrackedHangingProtocolKey = trackingKey;
  }, [hangingProtocol, isPhase4, groupedStudies]);

  const hangingProtocolLayouts: Nullable<Layouts> = useMemo(() => {
    return getHangingProtocolLayouts(hangingProtocol);
  }, [hangingProtocol]);

  // Publishes the derived value only. This must NOT clear the user's override: a manual layout the
  // user picked outranks the protocol on purpose, and this effect re-runs on every HP refetch.
  const publishLayouts = usePublishProtocolLayouts();
  useEffect(() => {
    publishLayouts(hangingProtocolLayouts);
  }, [publishLayouts, hangingProtocolLayouts]);

  const viewportsConfigurationsRecoilSelector = useViewportsConfigurationsSelector({ context });
  const resetToHP = useRecoilCallback(
    ({ set, reset }) =>
      () => {
        // the selector calls set on the base atom, so reset is safe here
        reset(viewportsConfigurationsRecoilSelector);
        // set() rather than reset(): reset() on an atom with a selector-based default produces a
        // DefaultValue, which broadcastChannelSynchronizerEffect silently skips. The write is scoped
        // to this case inside clearUserLayoutOverride.
        clearUserLayoutOverride(set, currentCaseId);
      },
    [viewportsConfigurationsRecoilSelector, currentCaseId]
  );

  return {
    hangingProtocol,
    loadingHangingProtocol: matchedHangingProtocolLoading,
    hangingProtocolViewportsConfigurations:
      hangingProtocolViewportsConfigurations != null
        ? hydrateViewportsConfigurations(hangingProtocolViewportsConfigurations, groupedStudies)
        : undefined,
    hangingProtocolLayouts,
    resetToHP,
  };
};

function validateViewType(viewType?: ViewType | null): ViewType {
  // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'ViewType | null | undefined' is not assignable to parameter of type 'ViewType'.
  if (!Object.values(ViewType).includes(viewType)) {
    logger.debug(`Invalid ViewType from Hanging Protocol viewType: ${viewType ?? 'null'}`);
  }
  return viewType ?? ViewType.TwoDDre;
}

export function dehydrateHangingProtocol(
  hangingProtocol: GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies']
): Nullable<DehydratedViewportsConfigurations> {
  return hangingProtocol?.viewportsConfigurations?.reduce((acc: any, { id, configuration }) => {
    return {
      ...acc,
      [id]: {
        ...configuration,
        viewType: validateViewType(configuration.viewType),
      },
    };
  }, {});
}

/**
 * Extracts layouts from a hanging protocol's `data.layouts` array.
 *
 * For stepped HPs the raw GQL response has `data.layouts: []` because each
 * step owns its own layouts. The `hangingProtocol` memo in `useHangingProtocol`
 * replaces `data.layouts` with the current step's layouts before this function
 * is called, so consumers always receive the correct value.
 */
export function getHangingProtocolLayouts(
  hangingProtocol: Nullable<
    GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies'] | HangingProtocol
  >
): Nullable<Layouts> {
  // Guard the full `data.layouts` path, not just a null protocol: a hanging
  // protocol can arrive non-null but without populated layouts (e.g. a partial
  // or auth-skipped response now that the HP match is its own query). Treat that
  // like "no protocol" so callers fall back to DEFAULT_LAYOUTS rather than
  // crashing the viewer render.
  const layouts = hangingProtocol?.data?.layouts;
  if (layouts == null) return;

  return Object.fromEntries(
    layouts.map(({ id, layout }: { id: string; layout: readonly number[] }) => [
      id,
      [layout[0], layout[1]], // converting the GraphQL array to a tuple
    ])
  );
}
